/**
 * Login/session helpers shared by the login flow.
 *
 * Extracted from hooks/useLogin.js (which re-exports them for backward
 * compatibility). Handle Cognito session checks, push token registration,
 * and role fetching/persistence.
 */
import Constants from 'expo-constants';
import { fetchAuthSession } from 'aws-amplify/auth';
import * as Notifications from 'expo-notifications';

import { isWeb } from '../platform';
import Util from '../util.js';
import {
  clearOrganizationAccess,
  setOrganizationAccess,
  setIsAdmin,
  setIsUserAdmin,
  setIsOrgAdmin,
  setIsAccountOwner,
  setIsMoneta,
} from '../storage';

/**
 * Check if user has an active session
 */
export async function isAlreadyAuthenticated() {
  try {
    const session = await fetchAuthSession();
    return Boolean(session?.tokens?.accessToken?.toString());
  } catch {
    return false;
  }
}

/**
 * Store push notification token on server
 */
export async function storePushNotificationKey() {
  if (isWeb()) return;

  try {
    let permissions = (await Notifications.getPermissionsAsync()).status;
    if (permissions !== 'granted') {
      permissions = (await Notifications.requestPermissionsAsync()).status;
    }
    if (permissions !== 'granted') return;

    const token = (
      await Notifications.getExpoPushTokenAsync({
        projectId: Constants.expoConfig?.extra?.eas?.projectId,
      })
    ).data;

    const requestData = { body: { pnTok: token } };
    await Util.post('Resty', '/items/addPushNotificationToken/', requestData);
  } catch (error) {
    Util.printAPIError(error);
  }
}

/**
 * Fetch user roles from API
 */
export async function getUserRoles(email = '') {
  await clearOrganizationAccess();
  const response = await Util.get('Resty', '/items/getUserRoles');

  if (response.data) {
    const organization = response.data.organization;
    const organizationAccess = response.data.organization_access ?? [];
    const isAdmin = response.data.Roles.includes('Analyst');
    const hasUserManagement = response.data.Roles.includes('UserAdmin');
    const hasOrgManagement = response.data.Roles.includes('OrgAdmin');
    const isAccountOwner = response.data.Roles.includes('AccountOwner');
    const isMonetaAdmin = response.data.Roles.includes('Moneta');

    await setOrganizationAccess(organizationAccess);
    await setIsAdmin(isAdmin);
    await setIsUserAdmin(hasUserManagement);
    await setIsOrgAdmin(hasOrgManagement);
    await setIsAccountOwner(isAccountOwner);
    await setIsMoneta(isMonetaAdmin);

    return { organization, isAdmin, hasUserManagement, hasOrgManagement, isAccountOwner, isMonetaAdmin };
  } else if (email !== '') {
    throw new Error('User is not yet registered to an organization');
  } else {
    throw new Error(undefined);
  }
}
